'use client'

import { useRouter } from 'next/navigation'
import { css, styled } from 'styled-components'

import { Button, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { PageWidth } from '@/components/PageWidth'

const framedStyles = css`
  margin: 3rem 0;
  background-color: ${Colors.Semantic.Error.background};
  border: 0.0625rem solid ${Colors.Semantic.Error.highlight};
  border-radius: 0.5rem;
`

const ErrorCard = styled.div<{ $framed: boolean }>`
  padding: 3rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  ${(props) => props.$framed && framedStyles}
`

const Title = styled.h1`
  ${Typography.Title3}
  margin: 0;
`

const Description = styled.p`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0;
`

/**
 * The AT&T access decision could not be read. A failure never grants access, so the listings
 * stay hidden; "Try again" calls `onRetry`, which asks for the decision again. `framed` draws the
 * card's own frame inside the page width; pass false when a parent card already frames it.
 */
export function StorefrontAccessCheckFailedCard({
  onRetry,
  framed = true,
}: {
  onRetry: () => void
  framed?: boolean
}) {
  const card = (
    <ErrorCard $framed={framed} data-testid="storefront-access-check-failed">
      <Title>
        <FormattedMessage id="StorefrontAccess.checkFailed.title" />
      </Title>
      <Description>
        <FormattedMessage id="StorefrontAccess.checkFailed.description" />
      </Description>
      <Button appearance="primary" onClick={onRetry}>
        <FormattedMessage id="StorefrontAccess.checkFailed.retry" />
      </Button>
    </ErrorCard>
  )
  return framed ? <PageWidth>{card}</PageWidth> : card
}

/** The storefront page's retry state: "Try again" re-runs the server page. */
export default function StorefrontAccessCheckFailed() {
  const router = useRouter()

  return <StorefrontAccessCheckFailedCard onRetry={() => router.refresh()} />
}
